/**
 * MomentoSubTabs — Barra de subpestañas por momento (Ceremonia / Cóctel /
 * Banquete / Baile). Se usa dentro de la pestaña "Peticiones" de un evento.
 *
 * Regla de visibilidad:
 *  - Baile siempre visible.
 *  - Los otros 3 buckets se auto-ocultan si tienen 0 peticiones, salvo que
 *    el usuario los haya forzado a mostrar/ocultar (override persistido).
 *  - Junto a cada tab hay un chevron desplegable con "Mostrar/Ocultar en este
 *    evento" para el override manual.
 */
import React, { useState, useMemo } from 'react';
import { Eye, EyeOff, Music2, Wine, UtensilsCrossed, PartyPopper, Settings } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  BUCKETS, BUCKET_LABELS, countByBucket, getVisibleBuckets,
} from '../utils/momentoBuckets';

const BUCKET_ICONS = {
  ceremonia: Music2,
  coctel: Wine,
  banquete: UtensilsCrossed,
  baile: PartyPopper,
};

const BUCKET_ACCENT = {
  ceremonia: 'text-purple-300 border-purple-500/60 bg-purple-500/10',
  coctel:    'text-amber-300  border-amber-500/60  bg-amber-500/10',
  banquete:  'text-emerald-300 border-emerald-500/60 bg-emerald-500/10',
  baile:     'text-pink-300   border-pink-500/60   bg-pink-500/10',
};

export const MomentoSubTabs = ({
  peticiones = [],
  activeBucket = 'baile',
  onChange,
  overrides = {},
  onOverridesChange,
  className = '',
  readOnly = false,
}) => {
  const counts = useMemo(() => countByBucket(peticiones), [peticiones]);
  const visible = useMemo(() => getVisibleBuckets(counts, overrides), [counts, overrides]);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Si el bucket activo deja de ser visible, cambiar a baile
  React.useEffect(() => {
    if (!visible.includes(activeBucket) && typeof onChange === 'function') {
      onChange('baile');
    }
  }, [visible, activeBucket, onChange]);

  const setOverride = (bucket, value) => {
    if (typeof onOverridesChange === 'function') {
      const next = { ...(overrides || {}) };
      if (value === null) {
        delete next[bucket];
      } else {
        next[bucket] = value;
      }
      onOverridesChange(next);
    }
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`} data-testid="momento-subtabs">
      {visible.map((bucket) => {
        const Icon = BUCKET_ICONS[bucket];
        const isActive = bucket === activeBucket;
        return (
          <button
            key={bucket}
            onClick={() => onChange && onChange(bucket)}
            className={`
              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-semibold
              transition-all
              ${isActive
                ? `${BUCKET_ACCENT[bucket]} shadow-[0_0_12px_rgba(255,255,255,0.15)] scale-105`
                : 'text-slate-400 border-slate-700 bg-slate-800/40 hover:text-slate-200 hover:border-slate-500'}
            `}
            data-testid={`momento-tab-${bucket}`}
            data-active={isActive}
          >
            <Icon className="w-4 h-4" />
            <span>{BUCKET_LABELS[bucket]}</span>
            <span className="text-[10px] opacity-75 tabular-nums">
              {counts[bucket] || 0}
            </span>
          </button>
        );
      })}

      {/* Menú de ajustes: forzar mostrar/ocultar buckets no-Baile
          (se oculta en modo readOnly, p.ej. Portal de Cliente) */}
      {readOnly ? null : (
      <DropdownMenu open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-200"
            title="Mostrar/Ocultar subpestañas en este evento"
            data-testid="momento-subtabs-settings-btn"
          >
            <Settings className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-slate-900 border-slate-700 text-slate-200 w-56"
          data-testid="momento-subtabs-settings-menu"
        >
          <DropdownMenuLabel className="text-xs text-slate-500">
            Subpestañas en este evento
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-slate-700" />
          {BUCKETS.filter((b) => b !== 'baile').map((b) => {
            const ov = overrides ? overrides[b] : undefined;
            const isVisible = visible.includes(b);
            const cnt = counts[b] || 0;
            return (
              <DropdownMenuItem
                key={b}
                onClick={() => {
                  // ciclo: auto (undefined) → false (ocultar) → true (forzar mostrar) → auto
                  const nextValue = isVisible ? false : (ov === false ? true : null);
                  setOverride(b, nextValue);
                }}
                className="text-xs cursor-pointer flex items-center justify-between"
                data-testid={`momento-toggle-${b}`}
              >
                <div className="flex items-center gap-2">
                  {isVisible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  <span>{BUCKET_LABELS[b]}</span>
                  <span className="text-slate-500">({cnt})</span>
                </div>
                <span className="text-[10px] text-slate-500">
                  {ov === true ? 'forzada' : ov === false ? 'oculta' : 'auto'}
                </span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
      )}
    </div>
  );
};

export default MomentoSubTabs;
